const OWN_TOOLTIP_CLASS = "has-own-tooltip";

const EXCLUDED_CELL_CLASSES = ["p-selection-column", "reorder-column", "go_to_details_column"] as const;

const isCellElement = (element: Element): element is HTMLTableCellElement =>
  element instanceof HTMLTableCellElement && (element.tagName === "TD" || element.tagName === "TH");

export const findTableCell = (target: EventTarget | null): HTMLTableCellElement | null => {
  if (!(target instanceof Element)) {
    return null;
  }

  const cell = target.closest("td, th");
  return cell && isCellElement(cell) ? cell : null;
};

export const isExcludedTableCell = (cell: HTMLTableCellElement): boolean => {
  if (cell.closest("tr.p-datatable-row-expansion")) {
    return true;
  }

  if (EXCLUDED_CELL_CLASSES.some(className => cell.classList.contains(className))) {
    return true;
  }

  return Boolean(cell.querySelector(`.${OWN_TOOLTIP_CLASS}`));
};

export const isElementTruncated = (element: HTMLElement): boolean => element.scrollWidth > element.clientWidth + 1;

/**
 * Walk from the event target up through the cell and return the first overflowing element.
 * Prefer an inner ellipsis wrapper when present; fall back to the cell itself (the usual clipper).
 */
export const findOverflowingElement = (target: EventTarget | null, cell: HTMLTableCellElement): HTMLElement | null => {
  if (!(target instanceof Node)) {
    return null;
  }

  let current: Node | null = target instanceof Element ? target : target.parentElement;
  while (current instanceof HTMLElement) {
    if (cell.contains(current) || current === cell) {
      if (isElementTruncated(current)) {
        return current;
      }
    }

    if (current === cell) {
      break;
    }

    current = current.parentElement;
  }

  return null;
};

export const getTruncatedCellTooltipText = (element: HTMLElement): string =>
  (element.innerText || element.textContent || "").trim();

export type TruncatedCellTooltipTarget = {
  element: HTMLElement;
  text: string;
};

export const resolveTruncatedCellTooltip = (target: EventTarget | null): TruncatedCellTooltipTarget | null => {
  const cell = findTableCell(target);
  if (!cell || isExcludedTableCell(cell)) {
    return null;
  }

  const overflowing = findOverflowingElement(target, cell);
  if (!overflowing) {
    return null;
  }

  const text = getTruncatedCellTooltipText(overflowing);
  if (!text) {
    return null;
  }

  return { element: overflowing, text };
};

export { OWN_TOOLTIP_CLASS };
